import * as z from 'zod';

export const GetTaxDocumentRequest = z.object({
  taxDocumentId: z
    .string()
    .describe('ID of the tax document to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetTaxDocumentResponse = z.object({
  _id: z
    .string()
    .describe('Tax document ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the tax document was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the tax document was last updated.')
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the tax document is updated.'
    )
    .optional(),
  accountId: z
    .string()
    .describe('ID of the Wix Payments account the tax document belongs to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  type: z.enum(['FORM_1099_K']).describe('Type of tax document.').optional(),
  period: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({ yearPeriod: z.never().optional() }),
        z.object({
          yearPeriod: z
            .object({
              year: z
                .number()
                .int()
                .describe(
                  'Calendar year the tax document covers. For example, `2024`.'
                )
                .optional(),
            })
            .describe('The tax document covers a full calendar year.'),
        }),
      ])
    )
    .describe('Period the tax document covers.')
    .optional(),
  downloadable: z
    .boolean()
    .describe(
      'Whether a file is available to download for the tax document. When `false`, calling Generate File Download Info returns an error.\n\nAt least one of `downloadable` and `problematic` is always `true`.'
    )
    .optional(),
  problematic: z
    .boolean()
    .describe(
      "Whether a problem was found with the tax document's file, for example incorrect data that's being corrected.\n\nAt least one of `downloadable` and `problematic` is always `true`, and both can be `true` at the same time."
    )
    .optional(),
  sourceType: z
    .enum(['PAYMENT', 'PAYOUT'])
    .describe("How the tax document's amounts are aggregated.")
    .optional(),
  displayName: z
    .string()
    .describe("Human-readable name for the tax document's file.")
    .min(1)
    .max(1000)
    .optional()
    .nullable(),
});
export const ListTaxDocumentsRequest = z.object({
  accountId: z
    .string()
    .describe(
      'ID of the Wix Payments account whose tax documents are retrieved.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Cursor paging options.')
        .optional(),
      type: z.enum(['FORM_1099_K']).optional(),
    })
    .optional(),
});
export const ListTaxDocumentsResponse = z.object({
  taxDocuments: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Tax document ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the tax document was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the tax document was last updated.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the tax document is updated.'
          )
          .optional(),
        accountId: z
          .string()
          .describe(
            'ID of the Wix Payments account the tax document belongs to.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        type: z
          .enum(['FORM_1099_K'])
          .describe('Type of tax document.')
          .optional(),
        period: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ yearPeriod: z.never().optional() }),
              z.object({
                yearPeriod: z
                  .object({
                    year: z
                      .number()
                      .int()
                      .describe(
                        'Calendar year the tax document covers. For example, `2024`.'
                      )
                      .optional(),
                  })
                  .describe('The tax document covers a full calendar year.'),
              }),
            ])
          )
          .describe('Period the tax document covers.')
          .optional(),
        downloadable: z
          .boolean()
          .describe(
            'Whether a file is available to download for the tax document. When `false`, calling Generate File Download Info returns an error.\n\nAt least one of `downloadable` and `problematic` is always `true`.'
          )
          .optional(),
        problematic: z
          .boolean()
          .describe(
            "Whether a problem was found with the tax document's file, for example incorrect data that's being corrected.\n\nAt least one of `downloadable` and `problematic` is always `true`, and both can be `true` at the same time."
          )
          .optional(),
        sourceType: z
          .enum(['PAYMENT', 'PAYOUT'])
          .describe("How the tax document's amounts are aggregated.")
          .optional(),
        displayName: z
          .string()
          .describe("Human-readable name for the tax document's file.")
          .min(1)
          .max(1000)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const GenerateFileDownloadInfoRequest = z.object({
  taxDocumentId: z
    .string()
    .describe('ID of the tax document to download a file for.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GenerateFileDownloadInfoResponse = z.object({
  downloadInfo: z
    .string()
    .describe("Information for downloading the tax document's file.")
    .optional(),
});
